import { CloudUploadOutlined, DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Input,
  message,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import * as Component from 'components';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  canRunVietnamIngest,
  getVietnamUploadDownloadUrl,
  ingestVietnamUpload,
  VietnamUploadItem,
  VietnamUploadListParams,
  VietnamUploadStatus,
} from 'service/api/vietnamUploadService';
import {
  useIngestVietnamUpload,
  useVietnamUploads,
  VIETNAM_UPLOADS_QUERY_KEYS,
} from 'hooks/useVietnamUploads';
import { accountStore } from 'store/accountStore';
import { parseVietnamUploadFileName } from 'utils/vietnamUploadFileType';

const { Title, Text } = Typography;

const STATUS_META: Record<VietnamUploadStatus, { color: string; label: string }> = {
  uploaded: { color: 'blue', label: 'Uploaded' },
  ingesting: { color: 'gold', label: 'Ingesting' },
  ingested: { color: 'green', label: 'Ingested' },
  failed: { color: 'red', label: 'Failed' },
};

const formatFileSize = (bytes: number | null): string => {
  if (!bytes || bytes <= 0) return '-';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${units[i]}`;
};

const DEFAULT_PAGE_SIZE = 20;

/**
 * Vietnam 업로드 목록 + Ingest(→ Video 에셋 + 트랜스코딩).
 *
 * 화면은 하나다. 자기 company만 보이는지 전 CP가 보이는지는 **서버가 호출자 권한으로 결정**하고,
 * 프론트는 스코프를 요청하지 않는다. 응답에 자기 것 아닌 company가 섞여 있으면 Company 컬럼을 연다.
 *
 * Asset·Action 열은 운영진(`admin` role **또는** `vietnam-uploads:ingest` code)에게만 만든다.
 * Ingest 는 거기에 더해 자기 company 레코드이거나 전 CP 자격일 때 활성이다 — 서버가 조회 범위와
 * 실행 범위를 같은 판정으로 묶었으므로 프론트도 한 값(`isIngestable`)으로 본다.
 *
 * 여러 건을 한 번에 처리할 수 있게 체크박스 선택 + 일괄 실행을 둔다. 서버에 벌크
 * 엔드포인트가 없어 개별 호출을 순차로 돌린다 — `runBulkIngest` 주석 참고.
 */
const VietnamUploadsList: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [statusFilter, setStatusFilter] = useState<VietnamUploadStatus | undefined>(undefined);
  const [search, setSearch] = useState('');
  const [companyFilter, setCompanyFilter] = useState<string | undefined>(undefined);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const queryClient = useQueryClient();

  const params: VietnamUploadListParams = {
    page,
    page_size: pageSize,
    status: statusFilter,
    search: search || undefined,
  };

  const { data, isFetching, refetch } = useVietnamUploads(params);
  const ingest = useIngestVietnamUpload();

  const myCompanySlug = accountStore.getAccount()?.company?.slug;

  /**
   * ODK 운영진(`admin` role) 여부. 전 CP 조회 자격 판정에도 쓴다.
   */
  const isOdkAdmin = accountStore.isOdkAdmin();

  /**
   * 운영진 자격 — `admin` role **또는** `vietnam-uploads:ingest` code (OR).
   *
   * 이 값 하나가 **Asset 열·Action 열·체크박스·일괄 실행**을 모두 좌우한다. 갈라 두면
   * "열은 없는데 체크박스로는 실행되는" 구멍이 생긴다.
   *
   * Asset·Action 은 운영 정보다 — 업로드한 쪽(CP)에게는 "내가 올린 파일이 목록에 있다"까지만
   * 보이면 되고, 그 파일이 어떤 에셋이 됐는지·인제스트를 언제 실행하는지는 아니다.
   *
   * role 과 code 를 AND 가 아니라 **OR** 로 묶는 이유: `admin` 은 이름이 정확히 `admin` 인
   * role 만 해당해서(`vietnam-admin` 은 false), AND 로 묶으면 code 만 부여받은 담당 계정에서
   * 열이 통째로 사라진다. 실제로 code 를 CP 에 주지 않는 것은 운영(role 구성)이 담보한다.
   */
  const isOperator = isOdkAdmin || accountStore.canIngestVietnamUpload();

  // VN 전용 계정은 `/assets/edit/:id`에 도달할 수 없다 (assets 메뉴 키 없음 + API가 allowlist 밖).
  // 링크를 걸면 눌러도 튕기므로 텍스트로만 보여준다.
  // 운영진에게 flag가 붙은 잘못된 설정에 대한 안전장치다 — 정상 설정이라면 항상 true다.
  const canOpenAsset = !accountStore.isLgChannelVietnam();

  const results = data?.results ?? [];
  const total = data?.count ?? 0;

  // 전 CP 자격은 세 경로로 성립한다 (BE `can_view_all_companies`와 같은 OR).
  //  1) `vietnam-uploads:view_all` code 보유
  //  2) `admin` role 보유 — 이제 `is_admin` 응답으로 직접 안다
  //  3) 위 둘을 못 읽었을 때의 보루: 서버가 타 company 레코드를 내려준 사실로 역추론.
  //     3번만으로는 "자격은 있는데 마침 자기 CP 업로드만 존재하는" 경우를 놓친다.
  const companySlugs = Array.from(new Set(results.map(row => row.company_slug).filter(Boolean)));
  const hasOtherCompanyRow = companySlugs.some(slug => slug !== myCompanySlug);
  const isCrossCompanyView =
    accountStore.canViewAllVietnamUploads() || isOdkAdmin || hasOtherCompanyRow;

  const visibleResults = companyFilter
    ? results.filter(row => row.company_slug === companyFilter)
    : results;

  /**
   * 이 행을 인제스트할 수 있는가 — 개별 버튼과 체크박스가 같은 판정을 쓴다.
   * 둘이 어긋나면 "선택은 되는데 실행하면 실패하는" 행이 생긴다.
   */
  const isIngestable = (record: VietnamUploadItem): boolean => {
    if (!isOperator) return false;
    // 자막은 인제스트 대상이 아니다. 이 경로는 create_video() + 트랜스코딩 Batch 제출이라,
    // 자막을 태우면 쓸모없는 Video 에셋과 실패하는 잡이 남는다. 자막은 받아서 기존 Content
    // 자막 화면으로 붙인다 — 서버에도 같은 방어가 없으므로 여기서 막는 것이 유일한 차단이다.
    if (parseVietnamUploadFileName(record.file_name).kind === 'subtitle') return false;
    if (!canRunVietnamIngest(record)) return false;
    return record.company_slug === myCompanySlug || isCrossCompanyView;
  };

  const selectableRows = visibleResults.filter(isIngestable);
  const selectedRows = visibleResults.filter(row => selectedIds.includes(row.id));

  /**
   * 일괄 인제스트. 서버에 벌크 엔드포인트가 없어 개별 호출을 **순차** 실행한다.
   * 병렬로 던지면 각 건이 S3 복사 + Batch 제출을 유발해 파이프라인에 몰린다.
   *
   * 중간에 실패해도 멈추지 않고 끝까지 돌린 뒤 결과를 모아 보여준다 —
   * 한 건 때문에 나머지가 안 돌아가면 다시 고르는 수고가 생긴다.
   */
  const runBulkIngest = async (rows: VietnamUploadItem[]): Promise<void> => {
    setBulkRunning(true);
    setBulkProgress({ done: 0, total: rows.length });
    const failures: { name: string; reason: string }[] = [];
    let created = 0;

    for (const [index, row] of rows.entries()) {
      try {
        const res = await ingestVietnamUpload(row.id);
        if (res.failures?.length) {
          failures.push({ name: row.file_name, reason: String(res.failures[0]) });
        } else {
          created += 1;
        }
      } catch (error: unknown) {
        const response = (error as { response?: { status?: number; data?: any } })?.response;
        const detail = response?.data?.detail || response?.data?.error || (error as Error)?.message;
        failures.push({
          name: row.file_name,
          reason: response?.status === 404 ? 'No access to this upload' : String(detail),
        });
      }
      setBulkProgress({ done: index + 1, total: rows.length });
    }

    setBulkRunning(false);
    setSelectedIds([]);
    // 건마다 무효화하면 목록을 그만큼 다시 불러온다. 끝난 뒤 한 번만 갱신한다.
    queryClient.invalidateQueries({ queryKey: VIETNAM_UPLOADS_QUERY_KEYS.all });

    if (!failures.length) {
      message.success(`Ingest started for ${created} file(s).`);
      return;
    }
    Modal.warning({
      title: `${created} succeeded, ${failures.length} failed`,
      width: 560,
      content: (
        <div>
          <p>The failed files stay in the list — you can retry them individually.</p>
          <ul style={{ paddingLeft: 18, margin: 0 }}>
            {failures.slice(0, 10).map(f => (
              <li key={f.name}>
                <strong>{f.name}</strong> — {f.reason}
              </li>
            ))}
          </ul>
          {failures.length > 10 && <p>…and {failures.length - 10} more.</p>}
        </div>
      ),
    });
  };

  const confirmBulkIngest = (): void => {
    Modal.confirm({
      title: `Ingest ${selectedRows.length} file(s)?`,
      content:
        'Each file is copied into the transcoding pipeline and creates an asset. ' +
        'This runs one at a time and cannot be undone.',
      okText: 'Ingest',
      onOk: () => runBulkIngest(selectedRows),
    });
  };

  const handleIngest = (record: VietnamUploadItem) => {
    ingest.mutate(record.id, {
      onSuccess: response => {
        const job = response.batch_jobs?.[0];
        if (job?.video_id) {
          message.success(`Asset #${job.video_id} created. Transcoding started.`);
        } else if (response.failures?.length) {
          message.error(`Ingest failed: ${response.failures[0] ?? 'Unknown error'}`);
        } else {
          message.success('Ingest started.');
        }
      },
      onError: (error: unknown) => {
        const response = (error as { response?: { status?: number; data?: any } })?.response;
        const status = response?.status;
        const detail = response?.data?.detail || response?.data?.error || (error as Error)?.message;
        if (status === 400) {
          message.error(detail || 'Already ingested or invalid key.');
        } else if (status === 403) {
          message.error('You do not have permission to ingest. (vietnam-uploads:ingest required)');
        } else if (status === 404) {
          message.error('You do not have access to this upload.');
        } else if (status === 500) {
          message.error('Ingest pipeline failed. Please retry.');
        } else {
          message.error(`Ingest failed: ${detail ?? 'Unknown error'}`);
        }
      },
    });
  };

  /**
   * 원본 다운로드. presigned URL 은 만료(600초)가 있어 **누를 때 발급받아 바로 연다** —
   * 목록 로드 시점에 미리 받아 두면 화면을 오래 열어둔 사이 만료된다.
   */
  const handleDownload = async (record: VietnamUploadItem): Promise<void> => {
    setDownloadingId(record.id);
    try {
      const { url } = await getVietnamUploadDownloadUrl(record.id);
      // 새 탭이 아니라 앵커로 연다 — 팝업 차단에 걸리지 않고 파일명을 지정할 수 있다.
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = record.file_name;
      anchor.rel = 'noopener';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    } catch (error: unknown) {
      const response = (error as { response?: { status?: number; data?: any } })?.response;
      const detail = response?.data?.detail || response?.data?.error || (error as Error)?.message;
      message.error(
        response?.status === 404
          ? 'No access to this upload.'
          : `Download failed: ${detail ?? 'Unknown error'}`
      );
    } finally {
      setDownloadingId(null);
    }
  };

  const renderIngestCell = (record: VietnamUploadItem) => {
    // 자막은 인제스트하지 않는다 — 받아서 기존 Content 자막 화면으로 붙인다.
    if (parseVietnamUploadFileName(record.file_name).kind === 'subtitle') {
      return (
        <Button
          size="small"
          icon={<DownloadOutlined />}
          loading={downloadingId === record.id}
          onClick={() => handleDownload(record)}
        >
          Download
        </Button>
      );
    }
    // 오래 멈춘 ingesting(retryable)은 아래 Retry Ingest 버튼으로 내려보낸다.
    if (record.status === 'ingesting' && !record.retryable) {
      return <Text type="secondary">Ingesting…</Text>;
    }
    if (record.status === 'ingested') {
      return <Text type="secondary">Done</Text>;
    }
    // 타 company 레코드는 전 CP 자격이 있어야 실행할 수 있다.
    // 서버가 조회 범위와 인제스트 범위를 같은 판정으로 묶었으므로 여기서도 같은 값을 쓴다.
    if (record.company_slug !== myCompanySlug && !isCrossCompanyView) {
      return (
        <Tooltip title="Only the owning CP can ingest this upload.">
          <Button size="small" icon={<CloudUploadOutlined />} disabled>
            Ingest
          </Button>
        </Tooltip>
      );
    }
    // uploaded → Ingest, failed | 오래 멈춘 ingesting → Retry Ingest
    const isThisRowIngesting = ingest.isPending && ingest.variables === record.id;
    return (
      <Button
        type="primary"
        size="small"
        icon={<CloudUploadOutlined />}
        loading={isThisRowIngesting}
        disabled={ingest.isPending || bulkRunning}
        onClick={() => handleIngest(record)}
      >
        {record.status === 'uploaded' ? 'Ingest' : 'Retry Ingest'}
      </Button>
    );
  };

  const columns: ColumnsType<VietnamUploadItem> = [
    {
      title: 'File Name',
      dataIndex: 'file_name',
      key: 'file_name',
      ellipsis: true,
      render: (value: string, record) => <span title={record.key}>{value}</span>,
    },
    // 자막이 섞이면서 종류 구분이 필요해졌다. Status(Uploaded/Ingested)와 열을 나눈 이유는
    // 자막의 Status 가 영원히 Uploaded 라, 한 열에 섞으면 "안 끝난 건"처럼 보이기 때문이다.
    {
      title: 'Type',
      key: 'type',
      width: 110,
      render: (_, record) =>
        parseVietnamUploadFileName(record.file_name).kind === 'subtitle' ? (
          <Tag color="purple">Subtitle</Tag>
        ) : (
          <Tag>Video</Tag>
        ),
    },
    // 전 CP가 내려온 계정에서만 노출한다 (자기 것만 보이는 계정에서는 항상 같은 값이라 무의미).
    ...(isCrossCompanyView
      ? ([
          {
            title: 'Company',
            dataIndex: 'company_slug',
            key: 'company_slug',
            width: 160,
            ellipsis: true,
            render: (value: string) => value || '-',
          },
        ] as ColumnsType<VietnamUploadItem>)
      : []),
    // 같은 회사 안에도 계정이 여럿이라 누가 올렸는지 구분이 필요하다.
    {
      title: 'Uploaded By',
      dataIndex: 'uploaded_by',
      key: 'uploaded_by',
      width: 200,
      ellipsis: true,
      render: (value: string | null) => value || '-',
    },
    {
      title: 'Size',
      dataIndex: 'file_size',
      key: 'file_size',
      width: 110,
      render: (value: number | null) => formatFileSize(value),
    },
    {
      title: 'Uploaded',
      dataIndex: 'created',
      key: 'created',
      width: 180,
      render: (value: string) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: VietnamUploadStatus) => {
        const meta = STATUS_META[status] ?? { color: 'default', label: status };
        return <Tag color={meta.color}>{meta.label}</Tag>;
      },
    },
    // 생성된 에셋은 **운영진에게만** 보여준다. CP에게는 자기 파일이 목록에 있다는 것까지가
    // 필요한 정보고, 어떤 에셋이 됐는지는 운영 정보다.
    //
    // 링크는 한 번 더 갈라진다. `/assets/edit/:id`는 `assets` 메뉴 키를 요구하고 그 화면의
    // API는 BE allowlist 밖이라, flag가 붙은 계정에서는 눌러도 튕기거나 403이 된다.
    // role을 더 줘도 열리지 않는다 — 막는 주체가 flag이기 때문이다. 그래서 id만 노출한다.
    ...(isOperator
      ? ([
          {
            title: 'Asset',
            key: 'asset',
            width: 130,
            render: (_, record) => {
              if (!record.video_id) {
                return <Text type="secondary">-</Text>;
              }
              if (canOpenAsset) {
                return (
                  <Button type="link" onClick={() => navigate(`/assets/edit/${record.video_id}`)}>
                    Asset #{record.video_id}
                  </Button>
                );
              }
              return <Text>Asset #{record.video_id}</Text>;
            },
          },
        ] as ColumnsType<VietnamUploadItem>)
      : []),
    // 운영진이 아닌 계정에는 컬럼 자체를 만들지 않는다 (조회 전용 화면이 된다).
    ...(isOperator
      ? ([
          {
            title: 'Action',
            key: 'action',
            width: 180,
            render: (_, record) => renderIngestCell(record),
          },
        ] as ColumnsType<VietnamUploadItem>)
      : []),
  ];

  return (
    <Component.BasePageComponent>
      <div style={{ padding: '24px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
          }}
        >
          <Title level={3} style={{ margin: 0 }}>
            Vietnam Video
          </Title>
          <Space>
            {accountStore.canAddVietnamUpload() && (
              <Button icon={<CloudUploadOutlined />} onClick={() => navigate('/assets/add-vn')}>
                Upload
              </Button>
            )}
            <Button icon={<ReloadOutlined />} loading={isFetching} onClick={() => refetch()}>
              Refresh
            </Button>
          </Space>
        </div>

        <Space style={{ marginBottom: '16px' }} wrap>
          <Input.Search
            allowClear
            placeholder="Search file name"
            style={{ width: 260 }}
            onSearch={value => {
              setPage(1);
              setSearch(value);
              setSelectedIds([]);
            }}
          />
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 160 }}
            value={statusFilter}
            onChange={value => {
              setPage(1);
              setStatusFilter(value);
              setSelectedIds([]);
            }}
            options={(Object.keys(STATUS_META) as VietnamUploadStatus[]).map(s => ({
              value: s,
              label: STATUS_META[s].label,
            }))}
          />
          {/*
            서버에 company 필터 파라미터가 없어서 현재 페이지 안에서만 걸러진다.
            그래서 전체 건수가 아니라 "이 페이지" 기준임을 라벨에 드러낸다.
            전 CP 전체에서 특정 company만 보려면 서버에 ?company= 지원이 필요하다.
          */}
          {isCrossCompanyView && (
            <Select
              allowClear
              placeholder="Company (this page)"
              style={{ width: 200 }}
              value={companyFilter}
              onChange={value => {
                setCompanyFilter(value);
                setSelectedIds([]);
              }}
              options={companySlugs.map(slug => ({ value: slug, label: slug }))}
            />
          )}
        </Space>

        {/* 선택이 있을 때만 나타난다 — 평소에는 화면을 차지하지 않는다. */}
        {isOperator && selectedRows.length > 0 && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: '16px' }}
            message={
              bulkRunning
                ? `Ingesting ${bulkProgress.done} of ${bulkProgress.total}…`
                : `${selectedRows.length} file(s) selected`
            }
            description={
              bulkRunning
                ? 'Running one at a time. Leaving this page will stop the remaining files.'
                : undefined
            }
            action={
              <Space>
                <Button
                  type="primary"
                  size="small"
                  icon={<CloudUploadOutlined />}
                  loading={bulkRunning}
                  onClick={confirmBulkIngest}
                >
                  Ingest selected
                </Button>
                <Button size="small" disabled={bulkRunning} onClick={() => setSelectedIds([])}>
                  Clear
                </Button>
              </Space>
            }
          />
        )}

        <Card bodyStyle={{ padding: 0 }}>
          <Table<VietnamUploadItem>
            rowKey="id"
            loading={isFetching}
            columns={columns}
            dataSource={visibleResults}
            // Ingest 권한이 없으면 고를 이유가 없으므로 체크박스를 아예 만들지 않는다.
            rowSelection={
              isOperator
                ? {
                    selectedRowKeys: selectedIds,
                    onChange: keys => setSelectedIds(keys as number[]),
                    getCheckboxProps: record => ({
                      disabled: bulkRunning || !isIngestable(record),
                      name: record.file_name,
                    }),
                    // 헤더 전체선택이 고를 수 있는 행만 대상으로 하도록 맞춘다.
                    selections: [
                      {
                        key: 'ingestable',
                        text: 'Select all ingestable on this page',
                        onSelect: () => setSelectedIds(selectableRows.map(r => r.id)),
                      },
                      Table.SELECTION_NONE,
                    ],
                  }
                : undefined
            }
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: true,
              onChange: (nextPage, nextSize) => {
                setPage(nextPage);
                setPageSize(nextSize);
                setCompanyFilter(undefined);
                // 페이지를 넘기면 안 보이는 행이 선택된 채로 남는다 — 혼동을 막으려 비운다.
                setSelectedIds([]);
              },
            }}
          />
        </Card>
      </div>
    </Component.BasePageComponent>
  );
};

export default observer(VietnamUploadsList);
